// @flow
import React, { Component } from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import classnames from 'classnames';
import theme from './FileUploadProgressAndErrors.scss';
import { progressCardFactory } from 'common/components/core/card';
import { ListCard } from 'common/components/core/card/ListCard';
import { Button, ButtonTypes } from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { Loader } from 'common/components/core/loader/Loader';
import ControlledSetInterval from 'common/utils/ControlledSetInterval';

const ErrorList = props => {
    const {
        uploadErrors,
        uploadWarnings,
        fail,
        theme,
        onCancel,
        onForceSubmit,
        t
    } = props;

    return fail ? (
        <div className={theme.errorListWrap}>
            <div className={theme.errorHeader}>{t('oopsMessage')}</div>
            {uploadWarnings?.length ? (
                <>
                    <div className={theme.errorList}>
                        {uploadWarnings.map((error, index) => {
                            return (
                                <ListCard
                                    className={classnames(theme.warning)}
                                    leftIcon={'error-calendar'}
                                    leftIconColor={'salmon-two'}
                                    key={index}
                                    title={error}
                                    theme={theme}
                                />
                            );
                        })}
                    </div>
                    <div className={theme.retryBtnContainer}>
                        <Button
                            buttonType={ButtonTypes.Flat}
                            color={dashboardColors.darkSkyBlue.name}
                            label={t('cancel')}
                            uppercase
                            semibold
                            theme={theme}
                            className={theme.actionButton}
                            onClick={onCancel}
                        />
                        <Button
                            buttonType={ButtonTypes.Solid}
                            color={dashboardColors.darkSkyBlue.name}
                            label={t('proceedAnyway')}
                            uppercase
                            semibold
                            theme={theme}
                            className={theme.actionButton}
                            onClick={onForceSubmit}
                        />
                    </div>
                </>
            ) : null}
            {uploadErrors?.length ? (
                <>
                    <div className={theme.errorList}>
                        {uploadErrors.map((error, index) => {
                            return (
                                <ListCard
                                    className={theme.error}
                                    leftIcon={'alert'}
                                    leftIconBgColor={'salmon-two'}
                                    key={index}
                                    title={error}
                                    theme={theme}
                                />
                            );
                        })}
                    </div>
                    <Button
                        buttonType={ButtonTypes.Solid}
                        color={dashboardColors.darkSkyBlue.name}
                        label={t('tryAgain')}
                        uppercase
                        semibold
                        theme={theme}
                        className={theme.retryButton}
                        onClick={onCancel}
                    />
                </>
            ) : null}
        </div>
    ) : null;
};

const RightIcons = ({ theme, inProgress }) => {
    return (
        <div className={theme.rightIcons}>
            <div className={theme.fileLoaderWrap}>
                <Loader
                    active={inProgress}
                    theme={theme}
                    className={theme.fileLoader}
                />
            </div>
        </div>
    );
};

const FileIcon = ({ theme }) => (
    <img src="/img/file.svg" className={theme.fileIcon} />
);

type Props = {
    theme: Object,
    fileName: string,
    statusText: string,
    isUploadInProgress: boolean,
    progressValue: number,
    fail: boolean,
    success: boolean,
    uploadErrors?: Array<string>,
    uploadWarnings?: Array<string>,
    progressIntervalTimeout?: number,
    onCancel: () => void,
    onForceSubmit: () => void
};

type State = {
    progressValue: number
};
const fileUploadProgressFactory = ({
    Fail = ErrorList,
    RightIcon = RightIcons,
    LeftIcon = FileIcon,
    Success = null
}) => {
    const UploadProgressCard = progressCardFactory({
        Fail,
        RightIcon,
        LeftIcon,
        Success
    });

    class FileUploadProgressAndErrors extends Component<Props, State> {
        currentTime: number;
        stopProgressBarHandler: ?Function;
        state: State;

        constructor(props) {
            super(props);
            this.currentTime = 0;
            this.state = {
                progressValue: 0
            };
        }

        componentDidMount() {
            const { isUploadInProgress, progressIntervalTimeout } = this.props;
            if (isUploadInProgress) {
                //ControlledSetInterval Scenario with autoTerminateOn4xx = false
                const poller = new ControlledSetInterval();
                this.stopProgressBarHandler = poller.initiateStatelessPolling({
                    callback: this.calculateProgressBar,
                    timeout: progressIntervalTimeout,
                    autoTerminateOn4xx: false
                });
            }
            this.calculateProgressBar();
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            const {
                isUploadInProgress,
                progressValue,
                progressIntervalTimeout
            } = this.props;
            const nextIsUploadInProgress = nextProps.isUploadInProgress;
            if (!progressValue) {
                if (nextIsUploadInProgress !== isUploadInProgress) {
                    if (
                        nextIsUploadInProgress &&
                        !this.stopProgressBarHandler
                    ) {
                        const poller = new ControlledSetInterval();
                        this.stopProgressBarHandler = poller.initiateStatelessPolling(
                            {
                                callback: this.calculateProgressBar,
                                timeout: progressIntervalTimeout,
                                autoTerminateOn4xx: false
                            }
                        );
                    }
                    if (!nextIsUploadInProgress) {
                        this.setState({
                            progressValue: 100
                        });
                        this.stopProgressBarHandler &&
                            this.stopProgressBarHandler();
                    }
                }
            }
        }

        componentWillUnmount() {
            this.stopProgressBarHandler && this.stopProgressBarHandler();
        }

        getMockProgressValue = currentTime => {
            if (this.props.success || this.props.fail) {
                return 0;
            }
            return 100 * (1 - Math.exp(-currentTime / 3000));
        };

        calculateProgressBar = () => {
            this.currentTime += 10;
            const progressValue = this.getMockProgressValue(this.currentTime);
            this.setState({
                progressValue
            });
        };

        render() {
            const {
                theme,
                fileName,
                statusText,
                isUploadInProgress,
                progressValue,
                onCancel,
                onForceSubmit,
                uploadErrors,
                uploadWarnings,
                fail,
                success,
                t
            } = this.props;

            return (
                <div className={theme.fileUploadWrap}>
                    <UploadProgressCard
                        t={t}
                        theme={theme}
                        title={fileName}
                        subtitle={statusText}
                        progressValue={
                            progressValue || this.state.progressValue
                        }
                        inProgress={isUploadInProgress}
                        onCancel={onCancel}
                        onForceSubmit={onForceSubmit}
                        uploadErrors={uploadErrors}
                        uploadWarnings={uploadWarnings}
                        fail={fail}
                        success={success}
                    />
                </div>
            );
        }
    }

    const ThemedFileUploadProgressAndErrors = compose(
        themr('FileUploadProgressAndErrors', theme),
        translate()
    )(FileUploadProgressAndErrors);

    FileUploadProgressAndErrors.defaultProps = {
        progressIntervalTimeout: 10
    };
    return ThemedFileUploadProgressAndErrors;
};
const FileUploadProgressAndErrors = fileUploadProgressFactory({});

export default FileUploadProgressAndErrors;
export { FileUploadProgressAndErrors, fileUploadProgressFactory };
